import { ButtonColumnContainer } from "./BoardInventoryStyles";
import Button from "../../common/Button";
import React, { useState } from "react";
import useBoardBin from "../../../utils/hooks/commonly-stored/uploaded-board/boardBin";
import { Board } from "../../../reducers/uploadedBoard";
import Modal from "../../common/Modal";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import InventoryBinForm from "../parts/InventoryBinForm";
import useUpdateInventoryBin from "../../../utils/hooks/inventory/updateInventoryBin";
import LoadingOverlay from "../../LoadingOverlay";
import { useCustomAlert } from "../../../utils/hooks/alerts";
import Link from "next/link";
import { binInventoryLink } from "../../../utils/links/inventory";
import { BinType, InventoryBin } from "../parts/Bins/inventoryBinTypes";
import SelectBin from "./SelectBin";

function createBoardBinTemplate(board: Board): InventoryBin {
  return {
    flashpcb_bin_barcode: "",
    bin_location: "",
    bin_name: `${board.project_name} Bin`,
    board_id: board.id,
    bin_type: BinType.JOB_TOTE,
    feeder_details: null,
    tray_details: null,
    can_allocate_from_this_bin: true,
  };
}

export default function BoardBinActions({
  board,
  onFinishedEditing,
}: {
  board: Board;
  onFinishedEditing: () => void;
}) {
  const { inventoryBin: boardBin, refreshBoardBin } = useBoardBin(board.id);
  const [binToEdit, setBinToEdit] = useState<InventoryBin | null>(null);
  const customAlert = useCustomAlert();

  const closeModal = () => {
    setBinToEdit(null);
    onFinishedEditing();
  };

  const onSuccess = () => {
    refreshBoardBin();
    closeModal();
  };

  const onError = (detail: string) => {
    closeModal();
    customAlert(detail);
  };

  const { saveBin, loading } = useUpdateInventoryBin(onSuccess, onError);

  const onSelectBin = (newValue: InventoryBin | null) => {
    if (newValue === null) {
      if (boardBin !== null) {
        saveBin({ ...boardBin, board_id: null }, false);
      }
    } else {
      saveBin({ ...newValue, board_id: board.id }, false);
    }
  };

  return (
    <>
      <LoadingOverlay loading={loading} text={"Updating bin..."} />
      <ButtonColumnContainer>
        {boardBin === null ? (
          <Button
            label={"Create job tote"}
            onClick={() => setBinToEdit(createBoardBinTemplate(board))}
            isSmallSize={true}
          />
        ) : (
          <>
            <Button
              label={"Edit bin"}
              onClick={() => setBinToEdit(boardBin)}
              isSmallSize={true}
            />
            <Link
              href={binInventoryLink(boardBin.flashpcb_bin_barcode)}
              target={"_blank"}
            >
              Go to bin inventory page
            </Link>
          </>
        )}
        <SelectBin onSelectBin={onSelectBin} />
      </ButtonColumnContainer>
      {binToEdit !== null && (
        <Modal isOpen onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal} fullScreen>
            <InventoryBinForm
              selectedBin={binToEdit}
              onSuccess={onSuccess}
              onCancel={closeModal}
              hideBoardSelection
              board={board}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}
    </>
  );
}
